/**
 * ToolsPanelGroup — collapsible group inside the right tools panel.
 *
 * Click the header to toggle the group open/closed. Header text uses
 * the supplied `color` for both the label and the chevron stroke,
 * which lets each group have its own accent (Navigate=amber,
 * Actions=indigo, etc.).
 */
import { useState, type ReactNode } from 'react'

interface ToolsPanelGroupProps {
  label: string
  color: string
  defaultOpen?: boolean
  children: ReactNode
}

export default function ToolsPanelGroup({
  label,
  color,
  defaultOpen = true,
  children,
}: ToolsPanelGroupProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div style={{ width: '100%' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          padding: '4px 8px',
          background: 'none',
          border: 'none',
          borderTop: '1px solid var(--border)',
          borderBottom: open ? '1px solid var(--border)' : 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        <span>{label}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          {open
            ? <polyline points="18 15 12 9 6 15" />
            : <polyline points="6 9 12 15 18 9" />}
        </svg>
      </button>
      {open && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            padding: '12px 0',
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}
